import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Empty, Input, Space, Spin, Typography } from 'antd';
import { CheckOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ageFromDob } from '@shared/common/utils/patient-age';
import { sexLabel } from '@shared/common/utils/patient-sex';
import { listMembersForPicker, searchMembersForPreview, type MemberSearchHit } from '@/api/briefingContext';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

const { Text } = Typography;

// Single-select patient picker for one practice. Picking a person is always a stacked drawer (founder, confirmed
// 2026-07-13 / 08-13 / 10-03 / 10-31), and it looks and behaves like AgentPickerDrawer: a header with the count and one
// search box, the list showing the moment it opens, click a row → selects and closes. The practice's patients are listed
// A to Z by last name before anything is typed; a name searches; a number is a patient id, which works without the
// patient-search permission and for one-digit ids. Results always belong to the practice asked for (review R9).

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 250;

/** One answer, tied to the text and the practice that asked for it, so it is never shown — or picked — for another. */
interface Answer {
  term: string;
  merchantId: number;
  hits: MemberSearchHit[];
  /** The practice's patient count; when browsing. */
  total: number | null;
  pageNo: number;
  failed: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** The practice whose patients are listed; results always belong to it (review R9). */
  merchantId: number;
  /** How the practice is named in the header, e.g. "Demo Oncology · #100". */
  practiceLabel?: string;
  selectedId?: number;
  onSelect: (patient: MemberSearchHit) => void;
  title?: string;
}

export const PatientPickerDrawer = observer(function PatientPickerDrawer({
  open,
  onClose,
  merchantId,
  practiceLabel,
  selectedId,
  onSelect,
  title = 'Choose a patient',
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const inputRef = useRef<{ focus: () => void } | null>(null);
  // Each new load makes older answers stale, so a slow first page never lands over a search.
  const requestToken = useRef(0);

  const term = query.trim();
  const typedId = /^\d+$/.test(term) ? Number(term) : null;
  const browsing = term === '';
  // Review M2 (2026-11-01): only the answer to THIS text at THIS practice is shown, clicked or picked by Enter. Rows of
  // the previous query vanish the moment the text or the practice changes, not when the next answer lands: "Alice" down
  // to one row, retyped as "Bob", Enter pressed before Bob's answer came — that picked Alice.
  const current = answer && answer.term === term && answer.merchantId === merchantId ? answer : null;
  const rows = typedId == null ? (current?.hits ?? []) : [];
  const waiting = open && typedId == null && current == null;
  const failed = current?.failed ?? false;
  const total = browsing ? (current?.total ?? null) : null;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setAnswer(null);
      return;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 220);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setLoadingMore(false);
    if (!open || typedId != null) {
      requestToken.current++;
      return;
    }
    const token = ++requestToken.current;
    const askedTerm = term;
    const askedMerchant = merchantId;
    const timer = setTimeout(async () => {
      try {
        const found = browsing
          ? await listMembersForPicker(askedMerchant, 1, PAGE_SIZE)
          : { hits: await searchMembersForPreview(askedMerchant, askedTerm, PAGE_SIZE), total: null };
        if (token !== requestToken.current) return;
        setAnswer({ term: askedTerm, merchantId: askedMerchant, hits: found.hits, total: found.total, pageNo: 1, failed: false });
      } catch {
        if (token !== requestToken.current) return;
        setAnswer({ term: askedTerm, merchantId: askedMerchant, hits: [], total: null, pageNo: 1, failed: true });
      }
    }, browsing ? 0 : DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, merchantId, term, typedId, browsing]);

  const showMore = async () => {
    if (!current || !browsing) return;
    const base = current;
    const sameAsked = (a: Answer | null) => a != null && a.term === base.term && a.merchantId === base.merchantId;
    const token = ++requestToken.current;
    setLoadingMore(true);
    try {
      const page = await listMembersForPicker(base.merchantId, base.pageNo + 1, PAGE_SIZE);
      if (token !== requestToken.current) return;
      setAnswer((a) => (sameAsked(a) ? { ...a!, hits: [...a!.hits, ...page.hits.filter((h) => !a!.hits.some((s) => s.id === h.id))], pageNo: a!.pageNo + 1 } : a));
    } catch {
      if (token === requestToken.current) setAnswer((a) => (sameAsked(a) ? { ...a!, failed: true } : a));
    } finally {
      if (token === requestToken.current) setLoadingMore(false);
    }
  };

  const pick = (patient: MemberSearchHit) => {
    onSelect(patient);
    onClose();
  };
  const pickTypedId = () => {
    if (typedId != null) pick({ id: typedId, firstName: `Patient #${typedId}` });
  };

  const headerBg = isDark ? colors.slate[800] : '#FFFFFF';
  const sectionBg = isDark ? colors.slate[900] : colors.slate[50];
  const borderColor = isDark ? colors.slate[700] : colors.slate[200];
  const mutedColor = isDark ? colors.slate[400] : colors.slate[500];
  const canShowMore = browsing && current != null && !current.failed && total != null && rows.length < total;

  const count =
    typedId != null ? null
      : browsing ? (total != null ? `${total.toLocaleString()} patients` : null)
      : current ? `${rows.length}${rows.length === PAGE_SIZE ? '+' : ''} found`
      : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(480px, 100vw)"
      closable={false}
      title={null}
      destroyOnHidden
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' }, header: { display: 'none' } }}
    >
      <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${borderColor}`, background: headerBg }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <UserOutlined style={{ fontSize: 16, color: colors.ink[600] }} />
          <Text strong style={{ fontSize: 15 }}>{title}</Text>
          {count && <span style={{ marginLeft: 'auto', fontSize: 12, color: mutedColor }}>{count}</span>}
        </div>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '4px 0 10px' }}>
          {`Patients of ${practiceLabel ?? `practice #${merchantId}`}, A to Z by last name.`}
        </Text>
        <Input
          ref={inputRef as never}
          allowClear
          placeholder="Search by name, or type a patient id…"
          prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={() => {
            if (typedId != null) pickTypedId();
            else if (!browsing && current != null && rows.length === 1) pick(rows[0]);
          }}
        />
      </div>

      <div style={{ flex: 1, overflow: 'auto', background: sectionBg, padding: '8px 0' }}>
        {typedId != null && (
          <PatientRow
            patient={{ id: typedId }}
            title={`Patient #${typedId}`}
            facts="Use this patient id. It works without patient search."
            isSelected={selectedId === typedId}
            isDark={isDark}
            onClick={pickTypedId}
          />
        )}

        {failed && (
          <div style={{ padding: '8px 16px' }}>
            <Alert
              type="warning"
              showIcon
              message="Patient search did not work."
              description="Type the patient's id in the search box instead."
            />
          </div>
        )}

        {rows.map((p) => (
          <PatientRow key={p.id} patient={p} isSelected={p.id === selectedId} isDark={isDark} onClick={() => pick(p)} />
        ))}

        {(waiting || loadingMore) && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: rows.length ? 12 : 40 }}>
            <Spin />
          </div>
        )}

        {current != null && !failed && typedId == null && rows.length === 0 && (
          <div style={{ padding: 24 }}>
            <Empty
              description={browsing ? 'This practice has no patients yet.' : `No patients match “${term}”.`}
            />
          </div>
        )}

        {canShowMore && !loadingMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
            <Button onClick={() => void showMore()}>
              {`Show ${Math.min(PAGE_SIZE, (total ?? 0) - rows.length)} more`}
            </Button>
          </div>
        )}
      </div>

      <div style={{ padding: '10px 20px', borderTop: `1px solid ${borderColor}`, background: headerBg }}>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Close</Button>
        </Space>
      </div>
    </Drawer>
  );
});

function PatientRow({
  patient,
  title,
  facts,
  isSelected,
  isDark,
  onClick,
}: {
  patient: MemberSearchHit;
  title?: string;
  facts?: string;
  isSelected: boolean;
  isDark: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  const baseBg = isDark ? colors.slate[900] : colors.slate[50];
  const hoverBg = isDark ? colors.slate[800] : '#FFFFFF';
  const selectedBg = isDark ? colors.ink[700] + '33' : colors.ink[50];
  const name = title ?? patientName(patient);
  const initials = `${patient.firstName?.trim()[0] ?? ''}${patient.lastName?.trim()[0] ?? ''}`.toUpperCase() || '#';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 20px',
        cursor: 'pointer',
        background: isSelected ? selectedBg : hover ? hoverBg : baseBg,
        borderLeft: isSelected ? `3px solid ${colors.ink[600]}` : '3px solid transparent',
        transition: 'background 80ms',
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: isDark ? colors.ink[700] : colors.ink[50],
          color: isDark ? colors.ink[100] : colors.ink[700],
          fontSize: 12,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {initials}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: isDark ? colors.slate[50] : colors.slate[900] }}>{name}</div>
        <div style={{ fontSize: 12, color: isDark ? colors.slate[400] : colors.slate[500], marginTop: 2 }}>
          {facts ?? patientFacts(patient)}
        </div>
      </div>
      {isSelected && <CheckOutlined style={{ color: colors.ink[600], fontSize: 14 }} />}
    </div>
  );
}

function patientName(p: MemberSearchHit): string {
  return [p.firstName, p.middleName, p.lastName].map((s) => s?.trim()).filter(Boolean).join(' ') || `Patient #${p.id}`;
}

/** What tells two patients apart: sex, age with the date of birth, and the id a run records. */
function patientFacts(p: MemberSearchHit): string {
  const age = ageFromDob(p.dob);
  const born = p.dob && dayjs(p.dob.substring(0, 10)).isValid() ? dayjs(p.dob.substring(0, 10)).format('D MMM YYYY') : null;
  return [
    sexLabel(p.gender),
    age != null ? `${age}${born ? ` (born ${born})` : ''}` : null,
    `#${p.id}`,
  ].filter(Boolean).join(' · ');
}
